import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { CalendarOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { globalAppInfo } from "../../config/globalAppInfo";
import "./PrivacyPolicy.css";

const TermsOfService = () => {
  const lastUpdated = "February 19, 2026";

  useEffect(() => {
    // Scroll to top immediately on component mount
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <div className="legal-page">
      {/* Header */}
      <div className="legal-header">
        <div className="container">
          {/* <Link to="/" className="legal-back-link">
            <ArrowLeftOutlined /> Back to Home
          </Link> */}
          <div className="legal-header-icon">📋</div>
          <h1>Terms of Service</h1>
          <p className="legal-subtitle">
            Please read these terms carefully before using our platform and services.
          </p>
          <p className="legal-last-updated">
            <CalendarOutlined /> Last Updated: {lastUpdated}
          </p>
        </div>
      </div>

      {/* Single Document */}
      <div className="legal-content">
        <div className="container">
          <div className="legal-document">

            <p className="legal-intro">
              Welcome to <strong>{globalAppInfo.name}</strong>. By accessing or using our platform, you agree
              to be bound by these Terms of Service. If you do not agree with any part of these terms,
              please dArrowLeftOutlinedo not use our platform. These terms govern your use of all services provided by{" "}
              <strong>{globalAppInfo.name}</strong>.
            </p>

            <div className="legal-toc">
              <h4>Table of Contents</h4>
              <ol style={{ listStyle: "decimal", paddingLeft: "20px", columns: 2, columnGap: "32px" }}>
                <li style={{ paddingLeft: 0 }}><a href="#tos-1">Eligibility</a></li>
                <li style={{ paddingLeft: 0 }}><a href="#tos-2">Account Registration</a></li>
                <li style={{ paddingLeft: 0 }}><a href="#tos-3">Code of Conduct</a></li>
                <li style={{ paddingLeft: 0 }}><a href="#tos-4">User Content</a></li>
                <li style={{ paddingLeft: 0 }}><a href="#tos-5">Privacy</a></li>
                <li style={{ paddingLeft: 0 }}><a href="#tos-6">Termination</a></li>
                <li style={{ paddingLeft: 0 }}><a href="#tos-7">Disclaimer of Warranties</a></li>
                <li style={{ paddingLeft: 0 }}><a href="#tos-8">Limitation of Liability</a></li>
                <li style={{ paddingLeft: 0 }}><a href="#tos-9">Governing Law</a></li>
              </ol>
            </div>

            <h2 id="tos-1">1. Eligibility</h2>
            <p>
              You must be at least 18 years old to use the platform. By using {globalAppInfo.name},
              you represent and warrant that you have the right, authority, and capacity to enter into
              this agreement and abide by all of the terms and conditions stated herein.
            </p>

            <h2 id="tos-2">2. Account Registration</h2>
            <p>
              You agree to provide accurate, current, and complete information during the registration
              process and to update such information to keep it accurate, current, and complete.
            </p>
            <p>
              You are responsible for maintaining the confidentiality of your account information and
              for all activities that occur under your account. You must notify us immediately of any
              unauthorized use of your account or any other breach of security. We will not be liable
              for any loss or damage arising from your failure to comply with this obligation.
            </p>

            <h2 id="tos-3">3. Code of Conduct</h2>
            <p>You agree to use the platform for lawful purposes only. Specifically, you agree that you will not:</p>
            <ul>
              <li>Engage in any activities that are harmful, offensive, or disruptive to other users of the platform.</li>
              <li>Use the platform to promote or engage in any illegal or unethical activities.</li>
              <li>Transmit any unsolicited or unauthorized advertising or promotional material.</li>
              <li>Impersonate any person or entity or misrepresent your affiliation with a person or entity.</li>
              <li>Interfere with or disrupt the integrity or performance of the platform or its related systems.</li>
              <li>Attempt to gain unauthorized access to the platform or its related systems or networks.</li>
            </ul>

            <h2 id="tos-4">4. User Content</h2>
            <p>
              By posting or sharing content on the platform, you grant {globalAppInfo.name} a worldwide,
              non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate,
              and distribute your content in any medium. This license is solely for the purpose of
              operating and providing our services.
            </p>
            <p>
              You are solely responsible for the content you post on the platform. You agree not to post
              any content that is defamatory, obscene, threatening, invasive of privacy, infringing of
              intellectual property rights, or otherwise injurious to third parties.
            </p>

            <h2 id="tos-5">5. Privacy</h2>
            <p>
              Your privacy is important to us. Please refer to our{" "}
              <Link to="/privacy-policy">Privacy Policy</Link> for details on how we collect, use, and
              protect your personal information. By using the platform, you consent to the collection
              and use of your information as described in our Privacy Policy.
            </p>

            <h2 id="tos-6">6. Termination</h2>
            <p>
              We reserve the right to terminate or suspend your account and access to the platform at our
              sole discretion, without notice, for conduct that we believe violates these Terms of Service
              or is harmful to other users, us, third parties, or for any other reason.
            </p>
            <p>
              Upon termination, your right to use the platform will immediately cease. All provisions of
              these Terms of Service which by their nature should survive termination shall survive, including
              ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
            </p>

            <h2 id="tos-7">7. Disclaimer of Warranties</h2>
            <p>
              The platform is provided on an "as is" and "as available" basis without any warranties of any
              kind, either express or implied, including but not limited to implied warranties of
              merchantability, fitness for a particular purpose, or non-infringement.
            </p>
            <p>
              We do not warrant that the platform will be uninterrupted or error-free, that defects will be
              corrected, or that the platform or the servers that make it available are free of viruses or
              other harmful components.
            </p>

            <h2 id="tos-8">8. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by applicable law, {globalAppInfo.name} shall not be liable
              for any indirect, incidental, special, consequential, or punitive damages, including but not
              limited to loss of profits, data, goodwill, or other intangible losses, arising out of or in
              connection with your use of, or inability to use, the platform.
            </p>
            <p>
              Our total cumulative liability for any claim arising out of or relating to these Terms or the
              platform shall not exceed the amount you have paid to us during the twelve (12) months
              immediately preceding the event giving rise to the claim.
            </p>

            <h2 id="tos-9">9. Governing Law</h2>
            <p>
              These Terms of Service are governed by and construed in accordance with the laws of India,
              without regard to its conflict of law provisions. Any disputes arising out of or relating to
              these Terms of Service shall be subject to the exclusive jurisdiction of the competent courts
              in India.
            </p>
            <p>
              If any provision of these Terms is found by a court to be invalid or unenforceable, the
              remaining provisions will remain in full force and effect.
            </p>

            <hr className="legal-divider" />

            <p style={{ fontSize: "15px", color: "#718096", fontStyle: "italic" }}>
              By using {globalAppInfo.name}, you acknowledge that you have read, understood, and agreed
              to these Terms of Service. If you have any questions, please contact us.
            </p>

            <div className="legal-contact-box">
              <p className="legal-contact-name">{globalAppInfo.name}</p>
              <p><EnvironmentOutlined className="legal-contact-icon" />{globalAppInfo.addressline1}</p>
              <p><MailOutlined className="legal-contact-icon" />Email: <a href={`mailto:${globalAppInfo.email1}`}>{globalAppInfo.email1}</a></p>
              <p><PhoneOutlined className="legal-contact-icon" />Phone: {globalAppInfo.mobile1}</p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
